import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  PERFIL_COPY,
  PerfilSeccionId,
} from '../../../data/perfil-microempresario.content';
import { UserService } from '../../../../services/user.service';
import { isPerfilJuridica } from '../../perfil-tipo.util';
import { PerfilSegmentoService } from '../../segmento/perfil-segmento.service';
import { AnalyticsTagService } from 'src/app/services/analytics-tag.service';

/**
 * Barra de acciones inferior (Figma) — compartida por secciones del perfil.
 * Antes del footer del sitio.
 */
@Component({
  selector: 'app-perfil-section-footer',
  templateUrl: './perfil-section-footer.component.html',
  styleUrls: ['./perfil-section-footer.component.scss'],
  standalone: false,
})
export class PerfilSectionFooterComponent {
  @Input() seccionId: PerfilSeccionId | null = null;
  /** Variante visual: secciones estándar vs pantalla "Define tu necesidad". */
  @Input() variant: 'default' | 'necesidad' = 'default';
  /** Muestra "Saltar por ahora" (p. ej. info opcional de negocio). */
  @Input() showSaltar = false;
  /** Muestra "Guardar información" como acción secundaria. */
  @Input() showGuardar = false;
  @Input() continuarDisabled = true;
  @Input() titleOverride: string | null = null;
  @Input() subtitleOverride: string | null = null;
  @Input() ctaLabelOverride: string | null = null;

  @Output() saltar = new EventEmitter<void>();
  @Output() guardar = new EventEmitter<void>();
  @Output() continuar = new EventEmitter<void>();

  readonly copy = PERFIL_COPY;

  private readonly iconMap: Record<PerfilSeccionId, string> = {
    'sobre-ti': 'assets/img/perfil/icon-sobre-ti.svg',
    'sobre-negocio': 'assets/img/perfil/icon-sobre-negocio.svg',
    'sobre-ingresos': 'assets/img/perfil/icon-sobre-ingresos.svg',
  };

  constructor(
    private readonly segmentoService: PerfilSegmentoService,
    private readonly userService: UserService,
    private readonly analyticsTag: AnalyticsTagService
  ) {}

  get titulo(): string {
    if (this.titleOverride) {
      return this.titleOverride;
    }
    if (this.esJuridica) {
      return this.copy.footerBar.titleJuridica ?? this.copy.footerBar.title;
    }
    return this.copy.footerBar.title;
  }

  get subtitulo(): string {
    if (this.subtitleOverride) {
      return this.subtitleOverride;
    }
    if (this.esJuridica && this.seccionId === 'sobre-ti') {
      return this.copy.footerBar['sobre-ti-juridica'];
    }
    if (this.esJuridica && this.seccionId === 'sobre-ingresos') {
      return this.copy.footerBar['sobre-ingresos-juridica'];
    }
    if (this.seccionId === 'sobre-ingresos' && this.segmentoService.esExpress) {
      return this.copy.footerBar['sobre-ingresos-express'];
    }
    if (this.seccionId) {
      return this.copy.footerBar[this.seccionId];
    }
    return '';
  }

  private get esJuridica(): boolean {
    return isPerfilJuridica(this.userService.getUser()?.tipoPersona);
  }

  get ctaLabel(): string {
    return this.ctaLabelOverride ?? this.copy.botones.continuar;
  }

  get iconSrc(): string | null {
    if (this.variant === 'necesidad' || !this.seccionId) {
      return null;
    }
    return this.iconMap[this.seccionId];
  }

  get footerId(): string {
    return this.seccionId ?? 'necesidad';
  }

  onSaltar(): void {
    this.analyticsTag.trackCtaClick({
      ctaLabel: this.copy.botones.saltarPorAhora,
      ctaSection: 'perfil-section-footer',
      ctaComponent: 'perfil-section-footer',
      ctaElementId: `btn-perfil-footer-saltar-${this.footerId}`,
      hu: '8099',
    });
    this.saltar.emit();
  }

  onGuardar(): void {
    this.guardar.emit();
  }

  onContinuar(): void {
    if (!this.continuarDisabled) {
      this.analyticsTag.trackCtaClick({
        ctaLabel: this.ctaLabel,
        ctaSection: 'perfil-section-footer',
        ctaComponent: 'perfil-section-footer',
        ctaElementId: `btn-perfil-footer-continuar-${this.footerId}`,
        hu: '8099',
      });
      this.continuar.emit();
    }
  }
}
